import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play } from "lucide-react";
import { useContent } from "../context/ContentContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppButton from "../components/WhatsAppButton";
import VideoModal from "../components/VideoModal";

const ORANGE = "#ff6a2b";
const GREEN = "#2ec9a8";
const YELLOW = "#d4b23c";

// ADN del equipo
const ADN_COLUMNS = [
  { key: "SER", color: ORANGE, subtitle: "Quién es — actitudes y valores", items: [
    { text: "Trabajo en equipo", highlight: true }, { text: "Liderazgo" }, { text: "Orientación al cliente" },
    { text: "Responsabilidad frente al sistema" }, { text: "Adaptabilidad / resiliencia" }, { text: "Empatía / I. emocional", highlight: true },
  ]},
  { key: "HACER", color: GREEN, subtitle: "Cómo actúa — habilidades", items: [
    { text: "Orientación al logro", highlight: true }, { text: "Planificación y organización" }, { text: "Iniciativa" },
    { text: "Disciplina / rigor" }, { text: "Toma de decisiones", highlight: true }, { text: "Pensamiento estratégico" },
  ]},
  { key: "SABER", color: YELLOW, subtitle: "Qué domina — conocimiento", items: [
    { text: "Desarrollo personal" }, { text: "Empoderamiento", highlight: true }, { text: "Habilidades analíticas" },
    { text: "Negociación / conflictos" }, { text: "Credibilidad técnica" }, { text: "Creatividad e innovación", highlight: true },
  ]},
];

// 5 necesidades
const NECESIDADES = [
  { n: "01", title: "Alineación y cascada de objetivos", desc: "Que desde el portero hasta el director entiendan lo mismo y remen en la misma dirección.", clients: "Levapan · Sodexo · Astara · Cenit", color: "#ff6a2b" },
  { n: "02", title: "Sentido de pertenencia e inclusión", desc: "Equipos dispersos en regiones o países que necesitan sentirse parte de algo real — no del organigrama.", clients: "Latam · Caracol · Abbott · Solística", color: "#2ec9a8" },
  { n: "03", title: "Inspiración y compromiso real", desc: "Salir del momento motivacional de 48 horas y construir compromisos que el equipo firma con su propia voz.", clients: "Siesalud · Janssen · SGS · Mobil", color: "#d4b23c" },
  { n: "04", title: "Cultura y liderazgo humano", desc: "Interiorizar valores, humanizar líderes, gestionar transiciones con inteligencia emocional.", clients: "Davivienda · Dr Reddys · Ecopetrol · Colsubsidio", color: "#8b7bff" },
  { n: "05", title: "Habilidades comerciales en acción", desc: "Fortalecer fuerzas de ventas con experiencias que combinan metodología, feedback y alto impacto.", clients: "SGS · Terpel · Green Coffee", color: "#4dc0ff" },
];

// Video thumbnail resolver (Vimeo via vumbnail, YouTube via img.youtube.com)
function getThumb(url) {
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/);
  if (yt) return `https://img.youtube.com/vi/${yt[1]}/hqdefault.jpg`;
  const vim = url.match(/vimeo\.com\/(\d+)/);
  if (vim) return `https://vumbnail.com/${vim[1]}.jpg`;
  return "";
}

// Groups de experiencias
const EXPERIENCIAS = [
  {
    key: "beat",
    name: "Kilombo Beat",
    color: "#e94a8a",
    lead: "El ritmo de tu equipo te lleva al logro.",
    tags: ["Alineación", "Compromiso", "Sentido de pertenencia"],
    description: "Teoría de percusión musical convertida en analogías del mundo empresarial para aprender a escucharse mutuamente y sincronizar equipos frente a sus desafíos, reconociendo su mutua dependencia.",
    items: [
      { brand: "Abbott Latam", video: "https://vimeo.com/359583405", why: "Inspirar equipos comerciales — reconocer su mutua dependencia — sincronizar procesos." },
      { brand: "Dell Latam", video: "https://vimeo.com/359586684", why: "Integración entre todos los equipos de Latinoamérica, reconocerse como un solo equipo y descubrir lo que son capaces de hacer trabajando juntos de manera sincronizada." },
      { brand: "GlaxoSmithKline (GSK) Latam", video: "https://vimeo.com/134862945", why: "Tener todo el equipo de Latinoamérica reunido para alinear propósito, reconocerse como un solo equipo y visibilizar su mutua dependencia para enfocarse en los logros." },
      { brand: "Unión de Colegios Bilingües de Bogotá (UCB)", video: "https://vimeo.com/514490871", why: "Cómo generar conciencia de que somos un solo equipo a pesar de ser de empresas y áreas diferentes. Alinear propósito organizacional y valorar la diferencia." },
    ],
  },
  {
    key: "team",
    name: "Kilombo Team",
    color: "#2ec9a8",
    lead: "Tu equipo es una banda tocando la misma partitura.",
    tags: ["Cultura", "Valores", "Inspiración", "Construcción de propósito"],
    description: "El equipo compone, escribe e interpreta su propia canción organizacional / himno corporativo y define quiénes son, para dónde van y cómo lograrán sus objetivos.",
    items: [
      { brand: "Casa Luker", video: "https://vimeo.com/275868513", why: "Construir un propósito organizacional entre todos. Saber de dónde venimos, para dónde vamos, qué meta lograremos, qué dificultades venceremos y qué valores tenemos como equipo." },
      { brand: "Seguridad Nápoles", video: "https://www.youtube.com/watch?v=RLh697GQaCI", why: "En convenciones, workshops o cualquier espacio de socialización y construcción de objetivos a largo plazo, este formato usa el poder de la composición musical para que los colaboradores construyan compromisos al componer e interpretar el himno de la empresa." },
    ],
  },
  {
    key: "impro",
    name: "Kilombo Impro",
    color: "#d4b23c",
    lead: "Arriesgarse a actuar diferente para tener resultados distintos.",
    tags: ["Adaptabilidad", "Escucha", "Foco en el logro", "Comunicación"],
    description: "Una técnica de entrenamiento escénico se convierte en hoja de ruta para entrenar equipos de alto desempeño para que desarrollen mayor número de soluciones y tengan el criterio para escoger la mejor en el menor tiempo posible.",
    items: [
      { brand: "Casa Luker", video: "https://vimeo.com/291738183", why: "Integrar áreas diferentes de una misma compañía, generar narrativas organizacionales desde lo experiencial y fortalecer el storytelling de la compañía." },
    ],
  },
  {
    key: "incite",
    name: "Kilombo Incite",
    color: "#8b7bff",
    lead: "Feedback directo y generación colectiva de compromisos.",
    tags: ["Retroalimentación", "Integración", "Generación de compromisos"],
    description: "Cuadrantes dibujados en el piso permiten conocer lo que un equipo piensa, siente y hace para reenfocar esfuerzos y facilitar logros.",
    items: [
      { brand: "SGS Colombia", video: "https://www.youtube.com/watch?v=EuNb6iFfoxI", why: "Equipos de alto desempeño se movilizan hacia objetivos, reconociéndose como equipo, fortaleciendo vínculos emocionales, generando feedback directo y acordando compromisos." },
    ],
  },
  {
    key: "graphics",
    name: "Kilombo Graphics",
    color: "#ff5c8a",
    lead: "Visualización de propósito, metas, dificultades y fortalezas.",
    tags: ["Capacitación", "Socialización de objetivos", "Cultura"],
    description: "Murales e infografías construidos por el propio equipo: la estrategia de la empresa en muros, carteleras, cartillas y pantallas.",
    items: [
      { brand: "Colsubsidio", video: "https://www.youtube.com/watch?v=xvYOCRbXYo0", why: "Todas las áreas de una empresa se reconocen como parte de una misma organización. Diseñadores, coreógrafos y músicos ayudan a conceptualizar visualmente entregables y socializar compromisos de cada área." },
      { brand: "Davivienda · Grupo Bolívar", video: "https://www.youtube.com/watch?v=wV3LKf18vIo", why: "Construir storytelling empresarial. Diseñadoras hacen infografías gigantes que se convierten en cartillas y piezas organizacionales para guiar acciones todo el año." },
    ],
  },
  {
    key: "challenge",
    name: "Kilombo Challenge",
    color: "#4dc0ff",
    lead: "El valor de ser multitask desde el rigor y valorar el proceso.",
    tags: ["Foco en el resultado", "Empoderamiento", "Resiliencia"],
    description: "La técnica de los malabares aplicada al mundo organizacional: valoración del proceso, aprender a recibir y entregar para el otro, rigor en el paso a paso, resiliencia frente al fracaso y celebrar los logros.",
    items: [
      { brand: "Terpel", video: "https://www.youtube.com/watch?v=KDpiM-LFkJo", why: "Desafiar un grupo de aliados estratégicos para que descubran que los desafíos se facilitan cuando reconocemos la importancia de los procesos, el rigor y la resiliencia frente al error." },
    ],
  },
  {
    key: "factor",
    name: "Factor K",
    color: "#ff9d5c",
    lead: "Construir compromisos y ser parte de logros impensables.",
    tags: ["Visibilidad de talentos", "Construcción de propósito", "Cultura"],
    description: "Construcción de propósito, visibilizando talentos al estilo de un reality televisivo, con convocatorias, selección y grabación de una canción que se convierte en el hit emocional y productivo de la empresa.",
    items: [
      { brand: "Meals de Colombia", video: "https://www.youtube.com/watch?v=RbXUKyXaUzA", why: "Alinear todos los grupos de interés de la empresa en función a un único propósito organizacional, visibilizando talentos al estilo de un reality televisivo, con convocatorias, selección y grabación de una canción que se convierte en el hit emocional y productivo de la empresa." },
    ],
  },
  {
    key: "noche",
    name: "La Noche de… (tu marca)",
    color: "#c084fc",
    lead: "Celebrar logros empresariales y reconocernos como equipo.",
    tags: ["Cultura", "Story telling empresarial", "Reconocimiento"],
    description: "Un espacio para reconocer logros, hitos, protagonistas y aportes de todos en las empresas. Todo a través de un viaje musical y narrativo entretenido y estratégico.",
    items: [
      { brand: "Asociación Médica de los Andes", video: "https://www.youtube.com/watch?v=RCw5QmC8aF4", why: "Un viaje a través de la música para celebrar, reconocer trayectorias y logros y fortalecer vínculos emocionales. No es una fiesta, es un show corporativo estratégico de alto impacto emocional." },
    ],
  },
  {
    key: "canto",
    name: "Yo Me Canto",
    color: "#5be0c3",
    lead: "El valor de aprender a exponernos y visibilizar habilidades.",
    tags: ["Cultura", "Clima organizacional", "Foco en el resultado", "Reconocimiento"],
    description: "Una banda en vivo y un jurado visibilizan habilidades blandas mientras celebramos. El Karaoke al servicio de la difusión y socialización de valores empresariales.",
    items: [
      { brand: "Compensar", video: "https://www.youtube.com/watch?v=HIpvKbXF5PU", why: "Celebración de grupos empresariales de alto desempeño. El valor de exponerse y hacer las cosas diferentes se potencializa bajo un formato televisivo tipo revista musical con músicos en vivo y un jurado reconocido que empodera valores de la compañía." },
    ],
  },
  {
    key: "accountability",
    name: "Kilombo Accountability",
    color: "#f97316",
    lead: "Como actúo en una dinámica, actúo en la empresa.",
    tags: ["Feedback de equipo", "Trabajo bajo presión", "Comunicación resolutiva"],
    description: "Más de 30 dinámicas de grupo para descubrir qué tipo de grupo somos. Descubrir cómo mi responsabilidad y acción contribuye al logro de todos.",
    items: [
      { brand: "Kilombo Accountability", video: "https://youtube.com/shorts/TzDNurlCMs8?feature=share", why: "Empoderar responsabilidades de cada cargo, área o ciudad en función a los objetivos de la organización y generar compromisos para llegar a las metas." },
    ],
  },
  {
    key: "eventos",
    name: "Eventos Corporativos",
    color: "#22d3ee",
    lead: "Experiencias de alto valor para fortalecer equipos y resultados.",
    tags: ["Eventos emocionales impactantes desde la música, el arte y la creatividad"],
    description: "Lanzamientos de marca y celebraciones de fechas especiales: mes de la familia, Día de los niños, Navidad y fin de año, aniversarios empresariales, etc.",
    items: [
      { brand: "Banco ING Colombia", video: "https://vimeo.com/243945161", why: "Lanzamiento de la marca en Colombia. Generar una experiencia de alto impacto en públicos de alto perfil." },
    ],
  },
  {
    key: "cuenta",
    name: "Cuenta Conmigo™",
    subtitle: "El sistema operativo de activación de cultura organizacional.",
    color: "#ff6a2b",
    text: "Cuenta Conmigo es un programa anual que activa la cultura organizacional desde lo individual hacia lo colectivo. Diseñamos y facilitamos experiencias que sostienen el pulso del equipo mes a mes, combinando disciplinas y recursos que casi nunca se tocan.",
    tags: ["Innovación", "Música", "Arte", "Creatividad", "Coaching empresarial"],
    isCuenta: true,
  },
];

// Cuenta Conmigo — mini timeline 12 meses (mismo diseño de la subpágina)
function CuentaConmigoCalendar() {
  const MONTHS = ["1","2","3","4","5","6","7","8","9","10","11","12"];
  const ROWS = [
    { name: "Estrategia", color: "#ff8a3c", bg: "rgba(255,138,60,0.18)", border: "rgba(255,138,60,0.55)", blocks: { 0: "Diagnóstico", 1: "Alineación", 5: "Seguimiento", 6: "Seguimiento", 11: "Cierre" } },
    { name: "Indicadores", color: "#d4b23c", dotAll: true },
    { name: "Encuentros", color: "#2ec9a8", bg: "rgba(46,201,168,0.18)", border: "rgba(46,201,168,0.55)", blocks: { 2: "Comité", 4: "Forecast", 6: "Comité", 8: "Resultados", 10: "Cierre" } },
    { name: "Reconocimiento", color: "#a78bfa", stars: [5, 9] },
  ];
  return (
    <div>
      {/* Mobile scroll hint */}
      <div className="md:hidden mb-3 flex items-center justify-end gap-2 font-mono-caps text-[10px] tracking-[0.2em] uppercase text-white/50">
        <span>Desliza</span>
        <ArrowRight className="w-3.5 h-3.5 animate-pulse" />
      </div>
      <div className="rounded-2xl border border-white/10 bg-neutral-950 p-4 md:p-6 overflow-x-auto">
        <div className="min-w-[880px]">
          <div className="grid" style={{ gridTemplateColumns: "160px repeat(12, minmax(0,1fr))" }}>
            <div />
            {MONTHS.map((m, i) => (
              <div key={i} className="text-center font-mono-caps text-[11px] tracking-[0.15em] uppercase text-white/40 pb-3 border-b border-white/10">{m}</div>
            ))}
          </div>
          {ROWS.map((row) => (
            <div key={row.name} className="grid items-center py-3 border-b border-white/5" style={{ gridTemplateColumns: "160px repeat(12, minmax(0,1fr))" }}>
              <div className="pr-3">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: row.color }} />
                  <span className="font-mono-caps text-[11px] md:text-xs tracking-[0.15em] uppercase text-white/85">{row.name}</span>
                </div>
              </div>
              {MONTHS.map((_, i) => (
                <div key={i} className="px-0.5 h-10 flex items-center justify-center">
                  {row.dotAll && (<span className="w-1.5 h-1.5 rounded-full" style={{ background: row.color, opacity: 0.7 }} />)}
                  {row.blocks?.[i] && (
                    <span
                      className="w-full h-8 rounded-md border flex items-center justify-center font-mono-caps tracking-[0.04em] uppercase text-white/90 px-0.5 text-center leading-[1.05] whitespace-nowrap"
                      style={{ background: row.bg, borderColor: row.border, fontSize: "9px" }}
                      title={row.blocks[i]}
                    >
                      {row.blocks[i]}
                    </span>
                  )}
                  {row.stars?.includes(i) && (
                    <svg viewBox="0 0 24 24" fill={row.color} className="w-5 h-5 drop-shadow-[0_0_6px_rgba(167,139,250,0.55)]">
                      <path d="M12 2l2.9 6.5 7.1.7-5.4 4.8 1.6 7L12 17.5 5.8 21l1.6-7L2 9.2l7.1-.7L12 2z" />
                    </svg>
                  )}
                </div>
              ))}
            </div>
          ))}
          <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2">
            {ROWS.map((row) => (
              <div key={row.name} className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-sm" style={{ background: row.color }} />
                <span className="font-mono-caps text-[10px] md:text-xs tracking-[0.15em] uppercase text-white/60">{row.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TeamsPage() {
  const { content, loading } = useContent();
  const [current, setCurrent] = useState(null);
  if (loading || !content) return <div className="min-h-screen bg-black flex items-center justify-center text-white/60 font-mono-caps tracking-widest">CARGANDO...</div>;

  return (
    <div className="relative min-h-screen bg-black text-white overflow-x-hidden">
      <Header />

      {/* HERO — título centrado + video grande, estilo MusicPage */}
      <section className="relative min-h-[55vh] flex items-center pt-32 pb-4 md:pt-40 md:pb-6 overflow-hidden bg-black">
        <div className="absolute inset-0 bg-gradient-to-b from-[#2ec9a8]/12 via-black/70 to-black" />
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 text-center w-full">
          <p className="font-mono-caps text-xs md:text-sm tracking-[0.3em] uppercase text-[#5be0c3]">Kilombo Teams</p>
          <h1 className="mt-6 font-display font-black text-[30px] leading-[1.05] sm:text-4xl md:text-5xl lg:text-6xl xl:text-[64px] tracking-tight max-w-5xl mx-auto" style={{ wordBreak: "keep-all", overflowWrap: "normal" }}>
            Transformamos grupos en <span className="text-gradient-teams">equipos</span>, y experiencias en <span className="text-gradient-teams">cultura</span>.
          </h1>
          <p className="mt-8 md:mt-10 max-w-3xl mx-auto text-white/95 text-xl md:text-2xl lg:text-3xl leading-snug font-medium" style={{ wordBreak: "keep-all", overflowWrap: "normal" }}>
            Usando el poder de la <span className="text-[#5be0c3] font-semibold">música, arte y creatividad</span>.
          </p>
        </div>
      </section>

      {/* Video hero grande */}
      <section className="relative bg-black pt-2 pb-16 md:pt-4 md:pb-24 px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="relative w-full aspect-video rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-black">
            <iframe
              src="https://player.vimeo.com/video/561546400?autoplay=1&muted=1&loop=1&background=0&byline=0&title=0&portrait=0&controls=1"
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title="Kilombo Teams — Reel"
            />
          </div>
        </div>
      </section>

      {/* MEDIO / PROPÓSITO (sin título encima) */}
      <section className="relative bg-neutral-950 py-16 md:py-20 px-6 border-y border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            <div className="rounded-2xl border border-white/10 bg-black p-6 md:p-8">
              <p className="font-mono-caps text-xs tracking-[0.3em] uppercase text-white/50">El medio</p>
              <p className="mt-6 text-white text-lg md:text-xl leading-snug font-medium">
                El poder transformador de la <span className="text-white font-semibold">música, el arte y la creatividad</span>.
              </p>
            </div>
            <div className="rounded-2xl border border-[#2ec9a8]/30 bg-gradient-to-br from-[#2ec9a8]/15 to-black p-6 md:p-8">
              <p className="font-mono-caps text-xs tracking-[0.3em] uppercase text-[#5be0c3]">El propósito</p>
              <p className="mt-6 text-white text-lg md:text-xl leading-snug font-medium">
                Potenciar <span className="text-[#5be0c3] font-semibold">personas y equipos</span> para facilitar sus logros, retos y metas.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FRASE CENTRADA — momento de respiración */}
      <section className="relative bg-black py-16 md:py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-white/90 text-base md:text-lg leading-relaxed">
            <span className="text-[#5be0c3] font-semibold">Las culturas no cambian en un evento.</span> Cambian cuando las personas viven un <span className="text-[#5be0c3] font-semibold">proceso</span>.
          </p>
        </div>
      </section>

      {/* LO QUE RESOLVEMOS — 5 necesidades */}
      <section className="relative bg-neutral-950 py-20 md:py-28 px-6 border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <p className="font-mono-caps text-xs tracking-[0.3em] uppercase text-[#d4b23c] mb-4">
            LO QUE RESOLVEMOS — <span className="text-white/50">NECESIDADES REALES DE CLIENTES</span>
          </p>
          <h2 className="font-display font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-4xl">
            <span className="text-white">5 necesidades.</span> <span className="text-[#e6c852]">Múltiples Experiencias.</span>
          </h2>

          <div className="mt-12 md:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {NECESIDADES.map((n) => (
              <div key={n.n} className="relative rounded-2xl border border-white/10 bg-black p-6 md:p-7 hover:border-white/25 transition-colors overflow-hidden">
                <span className="absolute -top-16 -right-16 w-40 h-40 rounded-full blur-3xl pointer-events-none opacity-25" style={{ background: n.color }} />
                <div className="relative">
                  <p className="font-display font-black text-2xl" style={{ color: n.color }}>{n.n}</p>
                  <h3 className="mt-4 font-display font-bold text-base md:text-lg leading-snug text-white pt-1">{n.title}</h3>
                  <p className="mt-3 text-white/60 text-sm leading-relaxed">{n.desc}</p>
                  <p className="mt-5 font-mono-caps text-[10px] tracking-[0.15em] uppercase text-white/40">{n.clients}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EXPERIENCIAS DE NUESTROS CLIENTES */}
      <section className="relative bg-black py-20 md:py-28 px-6">
        <div className="max-w-7xl mx-auto">
          <p className="font-mono-caps text-xs tracking-[0.3em] uppercase text-white/40 mb-4">— Experiencias de nuestros clientes —</p>
          <h2 className="font-display font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-4xl">
            Una Batería de Experiencias.<br />
            <span className="text-gradient-teams">Un formato para cada necesidad.</span>
          </h2>
          <p className="mt-6 max-w-3xl text-white/80 text-lg md:text-xl leading-snug font-medium">
            Si no existe, <span className="text-white">lo diseñamos juntos.</span>
          </p>

          <div className="mt-14 md:mt-20 space-y-16 md:space-y-24">
            {EXPERIENCIAS.map((g) => (
              <div key={g.key}>
                {/* Header con nombre + tags */}
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: g.color }} />
                      <h3 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-[0.05em] uppercase" style={{ color: g.color }}>
                        {g.name}
                      </h3>
                    </div>
                    {(g.lead || g.subtitle) && <p className="mt-3 text-white/90 text-lg md:text-xl leading-snug font-medium max-w-3xl">{g.lead || g.subtitle}</p>}
                    {g.tags && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {g.tags.map((t) => (
                          <span
                            key={t}
                            className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 font-mono-caps text-[10px] md:text-xs tracking-[0.18em] uppercase text-white/90"
                            style={{ borderColor: `${g.color}55`, background: `${g.color}15` }}
                          >
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: g.color }} />
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                    {g.description && !g.isCuenta && (
                      <p className="mt-5 max-w-3xl text-white/70 text-sm md:text-base leading-relaxed">{g.description}</p>
                    )}
                  </div>
                  {null}
                </div>

                {/* Cuenta Conmigo — párrafo + gráfica */}
                {g.isCuenta && (
                  <div>
                    <p className="max-w-4xl text-white/80 text-base md:text-lg leading-relaxed">{g.text}</p>
                    <div className="mt-8">
                      <CuentaConmigoCalendar />
                    </div>
                    <div className="mt-6">
                      <Link to="/teams/cuenta-conmigo" className="inline-flex items-center gap-2 font-mono-caps uppercase tracking-[0.15em] text-xs text-[#ff8a3c] hover:text-[#ffb46b] transition-colors">
                        Ver Cuenta Conmigo™ <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </div>
                )}

                {/* Grilla de videos */}
                {g.items?.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                    {g.items.map((it, i) => (
                      <div key={i} className="group rounded-2xl border border-white/10 bg-neutral-950 overflow-hidden hover:border-white/25 transition-colors flex flex-col">
                        <button
                          type="button"
                          onClick={() => setCurrent({ url: it.video, title: `${g.name} · ${it.brand}` })}
                          className="relative aspect-video overflow-hidden text-left"
                        >
                          <img
                            src={getThumb(it.video)}
                            alt={it.brand}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                            onError={(e) => { e.currentTarget.style.background = "#111"; }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="w-14 h-14 rounded-full bg-black/70 border border-white/30 flex items-center justify-center backdrop-blur transition-transform group-hover:scale-110" style={{ borderColor: `${g.color}88` }}>
                              <Play className="w-5 h-5 text-white ml-0.5" />
                            </span>
                          </div>
                        </button>
                        <div className="p-5 md:p-6 flex-1 flex flex-col">
                          <h4 className="font-display font-bold text-lg md:text-xl text-white leading-tight">{it.brand}</h4>
                          <p className="mt-3 text-white/60 text-sm leading-relaxed">
                            <span className="font-mono-caps text-[10px] tracking-[0.18em] uppercase" style={{ color: g.color }}>Por qué nos buscaron:</span>{" "}
                            <span className="text-white/75">{it.why}</span>
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ADN DEL EQUIPO */}
      <section className="relative bg-black py-20 md:py-28 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <p className="font-mono-caps text-xs tracking-[0.3em] uppercase text-[#ff8a3c] mb-4">
            ADN DEL EQUIPO™ · DIAGNÓSTICO <span className="text-white/50">SER · HACER · SABER</span>
          </p>
          <h2 className="font-display font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.02] max-w-4xl">
            Antes de proponer una experiencia, leemos al equipo.
          </h2>
          <p className="mt-5 max-w-2xl text-white/60 text-base md:text-lg leading-relaxed">
            Cada necesidad activa habilidades distintas. Elegimos la metodología según eso — nunca al revés.
          </p>

          <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-3 gap-4">
            {ADN_COLUMNS.map((col) => (
              <div key={col.key} className="rounded-2xl border border-white/10 bg-neutral-950 overflow-hidden">
                <div className="p-5 md:p-6 border-b" style={{ background: `linear-gradient(135deg, ${col.color}25, ${col.color}08)`, borderColor: `${col.color}30` }}>
                  <h3 className="font-heading text-2xl md:text-3xl tracking-[0.08em] text-white">{col.key}</h3>
                  <p className="mt-1 text-white/70 text-sm">{col.subtitle}</p>
                </div>
                <ul>
                  {col.items.map((it, i) => (
                    <li key={i} className={`relative px-5 md:px-6 py-3 border-b border-white/5 last:border-b-0 ${it.highlight ? "bg-black text-white font-medium" : "text-white/60"}`}>
                      {it.highlight && <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r" style={{ background: col.color }} />}
                      <span className="pl-1">{it.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="relative bg-black py-20 md:py-28 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center rounded-3xl border border-[#2ec9a8]/30 bg-gradient-to-br from-[#2ec9a8]/12 via-black to-black p-8 md:p-16">
          <h2 className="font-display font-black text-3xl md:text-4xl lg:text-5xl leading-tight text-white">
            ¿Qué quieres que tu equipo <span className="text-gradient-teams">sienta, piense y haga diferente</span>?
          </h2>
          <p className="mt-6 text-white/70 text-base md:text-lg max-w-xl mx-auto">
            Activemos transformaciones en tu equipo.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="btn-shine inline-flex items-center gap-3 rounded-full bg-white text-black pl-6 pr-5 py-3.5 font-mono-caps text-xs tracking-[0.2em] uppercase hover:bg-neutral-100 transition-colors">
              Quiero mover a mi equipo
              <span className="w-7 h-7 rounded-full bg-[#2ec9a8] text-black flex items-center justify-center"><ArrowRight className="w-3.5 h-3.5" /></span>
            </Link>
          </div>
        </div>
      </section>

      <Footer footer={content.footer} />
      <WhatsAppButton />

      <VideoModal
        open={!!current}
        onClose={() => setCurrent(null)}
        url={current?.url}
        title={current?.title}
      />
    </div>
  );
}
